import { useState } from "react";
import { copy } from "../copy";
import { executorStatusLine } from "../domain/executors";
import { useApp } from "../state/store";
import { TASK_TYPES, type ExecutorProfile, type TaskType } from "../types";

interface Draft {
  id: string | null;
  name: string;
  wakeUrl: string;
  headerName: string;
  headerValue: string;
  taskTypes: TaskType[];
  enabled: boolean;
}

const EMPTY: Draft = {
  id: null,
  name: "",
  wakeUrl: "",
  headerName: "",
  headerValue: "",
  taskTypes: [],
  enabled: true,
};

export function Executors() {
  const app = useApp();
  const executors = [...(app.state.executors ?? [])].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState("");
  const shownKey = app.freshKey;

  function edit(executor: ExecutorProfile) {
    setDraft({
      id: executor.id,
      name: executor.name,
      wakeUrl: executor.wakeUrl,
      headerName: executor.headerName,
      headerValue: "",
      taskTypes: [...executor.taskTypes],
      enabled: executor.enabled,
    });
  }

  async function save() {
    if (!draft) return;
    setBusy("save");
    const ok = await app.saveExecutor(draft);
    setBusy("");
    if (ok) setDraft(null);
  }

  async function run(id: string, action: "test" | "key" | "toggle", enabled = true) {
    setBusy(`${action}:${id}`);
    if (action === "test") await app.testExecutorWake(id);
    if (action === "key") await app.regenerateExecutorKey(id);
    if (action === "toggle") await app.setExecutorEnabled(id, enabled);
    setBusy("");
  }

  async function copyKey() {
    if (!shownKey) return;
    try {
      await navigator.clipboard.writeText(shownKey.apiKey);
      app.notify(copy.executors.copied);
    } catch {
      app.notify("复制没有成功，请在文本框里手动复制。", "danger");
    }
  }

  return (
    <div className="stack">
      <section className="panel">
        <p>{copy.executors.intro}</p>
        {app.devPreview ? <p className="notice">{copy.executors.local}</p> : null}
      </section>
      {shownKey ? (
        <section className="panel key-once" aria-label="执行者密钥">
          <p>{copy.executors.keyOnce}</p>
          <textarea readOnly rows={3} value={shownKey.apiKey} aria-label="执行者密钥" />
          <div className="row-actions">
            <button type="button" className="button line" onClick={() => void copyKey()}>{copy.executors.copyKey}</button>
            <button type="button" className="button ghost" onClick={app.dismissFreshKey}>{copy.close}</button>
          </div>
        </section>
      ) : null}
      {executors.length === 0 && !draft ? (
        <section className="panel empty-card">
          <h2>{copy.tasks.noExecutor}</h2>
          <p>{copy.executors.empty}</p>
          <button type="button" className="button primary" onClick={() => setDraft(EMPTY)}>{copy.executors.add}</button>
        </section>
      ) : (
        <>
          {!draft ? (
            <div className="library-bar">
              <span />
              <button type="button" className="button primary" onClick={() => setDraft(EMPTY)}>{copy.executors.add}</button>
            </div>
          ) : null}
          {executors.map((executor) => (
            <article key={executor.id} className="panel executor-card">
              <div className="executor-head">
                <h2>{executor.name}</h2>
                <span className="muted">{executor.keyHint || copy.dash}</span>
              </div>
              <p className="executor-status">{executorStatusLine(executor)}</p>
              <p className="muted clip">{executor.wakeUrl}</p>
              <p className="muted">{executor.headerSet ? `${executor.headerName} ${copy.executors.headerSaved}` : copy.executors.noHeader}</p>
              <div className="row-actions">
                <button type="button" className="button line" onClick={() => edit(executor)}>{copy.executors.edit}</button>
                <button
                  type="button"
                  className="button line"
                  disabled={busy === `toggle:${executor.id}`}
                  onClick={() => void run(executor.id, "toggle", !executor.enabled)}
                >
                  {executor.enabled ? copy.executors.disable : copy.executors.enable}
                </button>
                <button
                  type="button"
                  className="button line"
                  disabled={!executor.enabled || busy === `test:${executor.id}`}
                  onClick={() => void run(executor.id, "test")}
                >
                  {copy.executors.test}
                </button>
                <button
                  type="button"
                  className="button line"
                  title={copy.executors.regenWarn}
                  disabled={busy === `key:${executor.id}`}
                  onClick={() => void run(executor.id, "key")}
                >
                  {copy.executors.regen}
                </button>
              </div>
            </article>
          ))}
        </>
      )}
      {draft ? (
        <form
          className="panel executor-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <h2>{draft.id ? copy.executors.edit : copy.executors.add}</h2>
          <div className="form-grid">
            <label>
              {copy.executors.name}
              <input value={draft.name} maxLength={40} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </label>
            <label>
              {copy.executors.wakeUrl}
              <input
                value={draft.wakeUrl}
                inputMode="url"
                placeholder="https://"
                maxLength={500}
                onChange={(event) => setDraft({ ...draft, wakeUrl: event.target.value })}
              />
            </label>
            <label>
              {copy.executors.headerName}
              <input
                value={draft.headerName}
                maxLength={40}
                placeholder="Authorization"
                onChange={(event) => setDraft({ ...draft, headerName: event.target.value })}
              />
            </label>
            <label>
              {copy.executors.headerValue}
              <input
                value={draft.headerValue}
                type="password"
                autoComplete="off"
                maxLength={500}
                placeholder={draft.id && draft.headerName ? copy.executors.headerSaved : "Bearer …"}
                onChange={(event) => setDraft({ ...draft, headerValue: event.target.value })}
              />
            </label>
            <p className="wide muted">{copy.executors.headerHint}</p>
          </div>
          <fieldset>
            <legend>{copy.executors.types}</legend>
            <div className="choice-grid">
              {TASK_TYPES.map((type) => (
                <label key={type} className="check-line">
                  <input
                    type="checkbox"
                    checked={draft.taskTypes.includes(type)}
                    onChange={() => setDraft({
                      ...draft,
                      taskTypes: draft.taskTypes.includes(type)
                        ? draft.taskTypes.filter((item) => item !== type)
                        : [...draft.taskTypes, type],
                    })}
                  />
                  {type}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="row-actions">
            <button type="submit" className="button primary" disabled={busy === "save"}>{copy.executors.save}</button>
            <button type="button" className="button ghost" onClick={() => setDraft(null)}>{copy.executors.cancel}</button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
